import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, where, orderBy, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../configs/firebase';
import { EAuthData } from '../Provider/EAuthProvider';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';
import { 
  FaUserGraduate, FaChalkboardTeacher, FaClipboardList, FaExclamationTriangle,
  FaEye, FaCheck, FaTimes, FaCalendar, FaMapMarkerAlt, FaBuilding,
  FaClock, FaUsers, FaArrowUp, FaArrowDown, FaFilter, FaSearch,
  FaStar, FaChartLine, FaBell, FaFileAlt, FaUserCheck, FaUserTimes, 
  FaPlusCircle, FaHandshake, FaGlobe, FaBriefcase, FaAward, FaRocket, 
  FaBookmark, FaChartBar, FaChartPie, FaExpand, FaCompress, FaBullhorn
} from 'react-icons/fa';
import { 
  APPLICATION_STATUSES,
  getApplicationWithTimeoutInfo,
  updateApplicationStatusWithTimeout
} from '../../components/timeoutSystem';

const Dashboard = () => {
  const { user } = EAuthData();
  const [positions, setPositions] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyticsView, setAnalyticsView] = useState('overview'); // overview, trends, positions
  const [expandedChart, setExpandedChart] = useState(null);
  
  const [stats, setStats] = useState({
    totalPositions: 0,
    activePositions: 0,
    totalApplications: 0,
    pendingApplications: 0,
    awaitingResponseApplications: 0,
    confirmedHires: 0,
    rejectedApplications: 0,
    interviewApplications: 0,
    studentDeclinedApplications: 0,
    timeoutDeclinedApplications: 0,
    todayApplications: 0,
    urgentOffers: 0,
    conversionRate: 0,
    offerAcceptanceRate: 0
  });
  
  const [analyticsData, setAnalyticsData] = useState({
    monthlyApplications: [],
    applicationsByStatus: [],
    positionPerformance: [],
    applicationTrends: [],
    hiringFunnel: []
  });
  
  const [recentActivity, setRecentActivity] = useState([]);
  const [topPositions, setTopPositions] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [updating, setUpdating] = useState(false);

  // Colors for charts
  const COLORS = ['#f97316', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  const fetchData = async () => {
    if (!user?.userd?.uid) return;

    try {
      setLoading(true);
      
      // Fetch positions
      const positionsRef = collection(db, 'positions');
      const positionsQuery = query(
        positionsRef,
        where('employer_id', '==', user.userd.uid),
        orderBy('createdAt', 'desc')
      );
      const positionsSnapshot = await getDocs(positionsQuery);
      const positionsData = positionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPositions(positionsData);

      // Fetch applications
      const applicationsRef = collection(db, 'applications');
      const applicationsQuery = query(
        applicationsRef,
        where('employerId', '==', user.userd.uid),
        orderBy('appliedAt', 'desc')
      );
      const applicationsSnapshot = await getDocs(applicationsQuery);
      const applicationsData = applicationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setApplications(applicationsData);

      // Process analytics data
      processAnalyticsData(applicationsData, positionsData);
      calculateStats(applicationsData, positionsData);

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch and process analytics data
  useEffect(() => {
    fetchData();
  }, [user]);

  const processAnalyticsData = (applicationsData, positionsData) => {
    // Add timeout info to applications data
    const applicationsWithTimeout = applicationsData.map(app => 
      getApplicationWithTimeoutInfo(app)
    );

    // Monthly applications data for the last 6 months
    const monthlyData = [];
    const currentDate = new Date();
    
    for (let i = 5; i >= 0; i--) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const monthName = date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      const nextMonth = new Date(date.getFullYear(), date.getMonth() + 1, 1);
      
      const monthApplications = applicationsWithTimeout.filter(app => {
        const appDate = new Date(app.appliedAt);
        return appDate >= date && appDate < nextMonth;
      });
      
      monthlyData.push({
        month: monthName,
        applications: monthApplications.length,
        accepted: monthApplications.filter(app => app.status === APPLICATION_STATUSES.ACCEPTED).length,
        studentAccepted: monthApplications.filter(app => app.status === APPLICATION_STATUSES.STUDENT_ACCEPTED).length,
        rejected: monthApplications.filter(app => app.status === APPLICATION_STATUSES.REJECTED).length,
        pending: monthApplications.filter(app => app.status === APPLICATION_STATUSES.PENDING).length,
        interviews: monthApplications.filter(app => app.status === APPLICATION_STATUSES.INTERVIEW).length
      });
    }

    // Updated applications by status for pie chart
    const statusData = [
      { 
        name: 'Pending', 
        value: applicationsWithTimeout.filter(app => app.status === APPLICATION_STATUSES.PENDING).length, 
        color: '#f59e0b' 
      },
      { 
        name: 'Interview', 
        value: applicationsWithTimeout.filter(app => app.status === APPLICATION_STATUSES.INTERVIEW).length, 
        color: '#8b5cf6' 
      },
      { 
        name: 'Awaiting Response', 
        value: applicationsWithTimeout.filter(app => app.status === APPLICATION_STATUSES.ACCEPTED).length, 
        color: '#3b82f6' 
      },
      { 
        name: 'Hired', 
        value: applicationsWithTimeout.filter(app => app.status === APPLICATION_STATUSES.STUDENT_ACCEPTED).length, 
        color: '#10b981' 
      },
      { 
        name: 'Student Declined', 
        value: applicationsWithTimeout.filter(app => app.status === APPLICATION_STATUSES.STUDENT_DECLINED).length, 
        color: '#f97316' 
      },
      { 
        name: 'Rejected', 
        value: applicationsWithTimeout.filter(app => app.status === APPLICATION_STATUSES.REJECTED).length, 
        color: '#ef4444' 
      },
      { 
        name: 'Auto-Declined', 
        value: applicationsWithTimeout.filter(app => app.status === APPLICATION_STATUSES.TIMEOUT_DECLINED).length, 
        color: '#6b7280' 
      }
    ].filter(item => item.value > 0);

    // Updated position performance data
    const positionPerformanceData = positionsData.map(position => {
      const positionApps = applicationsWithTimeout.filter(app => app.positionId === position.id);
      const confirmedHires = positionApps.filter(app => app.status === APPLICATION_STATUSES.STUDENT_ACCEPTED).length;
      const conversionRate = positionApps.length > 0 ? (confirmedHires / positionApps.length * 100) : 0;
      
      return {
        name: position.name.length > 20 ? position.name.substring(0, 20) + '...' : position.name,
        fullName: position.name,
        applications: positionApps.length,
        accepted: positionApps.filter(app => app.status === APPLICATION_STATUSES.ACCEPTED).length,
        confirmedHires: confirmedHires,
        conversionRate: conversionRate,
        location: position.location
      };
    }).sort((a, b) => b.applications - a.applications).slice(0, 8);

    // Weekly application trends (last 8 weeks)
    const weeklyTrends = [];
    for (let i = 7; i >= 0; i--) {
      const weekStart = new Date(currentDate.getTime() - i * 7 * 24 * 60 * 60 * 1000);
      const weekEnd = new Date(weekStart.getTime() + 7 * 24 * 60 * 60 * 1000);
      const weekLabel = `Week ${8 - i}`;
      
      const weekApplications = applicationsWithTimeout.filter(app => {
        const appDate = new Date(app.appliedAt);
        return appDate >= weekStart && appDate < weekEnd;
      });
      
      weeklyTrends.push({
        week: weekLabel,
        applications: weekApplications.length,
        accepted: weekApplications.filter(app => app.status === APPLICATION_STATUSES.ACCEPTED).length,
        confirmedHires: weekApplications.filter(app => app.status === APPLICATION_STATUSES.STUDENT_ACCEPTED).length
      });
    }

    // Updated hiring funnel data
    const totalApps = applicationsWithTimeout.length;
    const interviewApps = applicationsWithTimeout.filter(app => app.status === APPLICATION_STATUSES.INTERVIEW).length;
    const offersExtended = applicationsWithTimeout.filter(app => 
      app.status === APPLICATION_STATUSES.ACCEPTED || 
      app.status === APPLICATION_STATUSES.STUDENT_ACCEPTED || 
      app.status === APPLICATION_STATUSES.STUDENT_DECLINED ||
      app.status === APPLICATION_STATUSES.TIMEOUT_DECLINED
    ).length;
    const confirmedHires = applicationsWithTimeout.filter(app => app.status === APPLICATION_STATUSES.STUDENT_ACCEPTED).length;
    
    const funnelData = [
      { stage: 'Applications', count: totalApps, percentage: 100 },
      { 
        stage: 'Interviews', 
        count: interviewApps + offersExtended, 
        percentage: totalApps > 0 ? ((interviewApps + offersExtended) / totalApps * 100) : 0 
      },
      { 
        stage: 'Offers Extended', 
        count: offersExtended, 
        percentage: totalApps > 0 ? (offersExtended / totalApps * 100) : 0 
      },
      { 
        stage: 'Confirmed Hires', 
        count: confirmedHires, 
        percentage: totalApps > 0 ? (confirmedHires / totalApps * 100) : 0 
      }
    ];

    setAnalyticsData({
      monthlyApplications: monthlyData,
      applicationsByStatus: statusData,
      positionPerformance: positionPerformanceData,
      applicationTrends: weeklyTrends,
      hiringFunnel: funnelData
    });
  };

  const calculateStats = (applicationsData, positionsData) => {
    // Add timeout info to applications
    const applicationsWithTimeout = applicationsData.map(app => 
      getApplicationWithTimeoutInfo(app)
    );

    const activePositions = positionsData.filter(pos => pos.isAdvertised).length;
    const pendingApps = applicationsWithTimeout.filter(app => app.status === APPLICATION_STATUSES.PENDING).length;
    const awaitingResponseApps = applicationsWithTimeout.filter(app => app.status === APPLICATION_STATUSES.ACCEPTED).length;
    const confirmedHires = applicationsWithTimeout.filter(app => app.status === APPLICATION_STATUSES.STUDENT_ACCEPTED).length;
    const rejectedApps = applicationsWithTimeout.filter(app => app.status === APPLICATION_STATUSES.REJECTED).length;
    const interviewApps = applicationsWithTimeout.filter(app => app.status === APPLICATION_STATUSES.INTERVIEW).length;
    const studentDeclinedApps = applicationsWithTimeout.filter(app => app.status === APPLICATION_STATUSES.STUDENT_DECLINED).length;
    const timeoutDeclinedApps = applicationsWithTimeout.filter(app => app.status === APPLICATION_STATUSES.TIMEOUT_DECLINED).length;
    
    // Today's applications
    const today = new Date();
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const todayApps = applicationsWithTimeout.filter(app => 
      new Date(app.appliedAt) >= todayStart
    ).length;

    // Applications needing urgent attention (accepted offers expiring soon)
    const urgentOffers = applicationsWithTimeout.filter(app => 
      app.status === APPLICATION_STATUSES.ACCEPTED && 
      app.timeoutInfo?.isUrgent
    ).length;

    // Updated conversion rate (confirmed hires / total applications)
    const conversionRate = applicationsWithTimeout.length > 0 ? (confirmedHires / applicationsWithTimeout.length * 100) : 0;

    // Offer acceptance rate (student_accepted / (student_accepted + student_declined + timeout_declined))
    const totalOfferResponses = confirmedHires + studentDeclinedApps + timeoutDeclinedApps;
    const offerAcceptanceRate = totalOfferResponses > 0 ? (confirmedHires / totalOfferResponses * 100) : 0;

    setStats({
      totalPositions: positionsData.length,
      activePositions,
      totalApplications: applicationsWithTimeout.length,
      pendingApplications: pendingApps,
      awaitingResponseApplications: awaitingResponseApps,
      confirmedHires: confirmedHires,
      rejectedApplications: rejectedApps,
      interviewApplications: interviewApps,
      studentDeclinedApplications: studentDeclinedApps,
      timeoutDeclinedApplications: timeoutDeclinedApps,
      todayApplications: todayApps,
      urgentOffers: urgentOffers,
      conversionRate,
      offerAcceptanceRate
    });

    // Updated top positions calculation
    const positionApplicationCounts = positionsData.map(position => {
      const applicationCount = applicationsWithTimeout.filter(app => app.positionId === position.id).length;
      const confirmedHires = applicationsWithTimeout.filter(app => 
        app.positionId === position.id && app.status === APPLICATION_STATUSES.STUDENT_ACCEPTED
      ).length;
      return { 
        ...position, 
        applicationCount,
        confirmedHires
      };
    }).sort((a, b) => b.applicationCount - a.applicationCount).slice(0, 5);
    setTopPositions(positionApplicationCounts);

    // Updated recent activity
    const activity = applicationsWithTimeout.slice(0, 8).map(app => ({
      id: app.id,
      type: 'application',
      message: `${app.studentName} applied for ${app.positionTitle}`,
      time: app.appliedAt,
      status: app.status,
      studentName: app.studentName,
      positionTitle: app.positionTitle,
      isUrgent: app.timeoutInfo?.isUrgent || false
    }));
    setRecentActivity(activity);
  };

  // Enhanced update application status using timeout system
  const updateApplicationStatus = async (applicationId, newStatus) => {
    setUpdating(true);
    try {
      // Use the timeout system function instead of direct update
      await updateApplicationStatusWithTimeout(
        applicationId, 
        newStatus, 
        user.userd.uid
      );

      // Update local state with timeout info
      setApplications(prev => 
        prev.map(app => 
          app.id === applicationId 
            ? getApplicationWithTimeoutInfo({
                ...app, 
                status: newStatus, 
                updatedAt: new Date().toISOString(),
                // Add timeout fields if accepting
                ...(newStatus === APPLICATION_STATUSES.ACCEPTED && {
                  acceptedAt: new Date().toISOString(),
                  studentResponseDeadline: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
                  timeoutEnabled: true
                })
              })
            : app
        )
      );

      // Recalculate stats
      const updatedApplications = applications.map(app => 
        app.id === applicationId ? { ...app, status: newStatus } : app
      );
      calculateStats(updatedApplications, positions);

    } catch (error) {
      console.error('Error updating application:', error);
      alert('Error updating application. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  // Filter applications
  const filteredApplications = applications.filter(app => {
    const matchesFilter = filter === 'all' || app.status === filter;
    const matchesSearch = searchTerm === '' || 
      app.studentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.positionTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.studentEmail?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesFilter && matchesSearch;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case APPLICATION_STATUSES.PENDING: return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case APPLICATION_STATUSES.ACCEPTED: return 'bg-blue-100 text-blue-800 border-blue-200';
      case APPLICATION_STATUSES.STUDENT_ACCEPTED: return 'bg-green-100 text-green-800 border-green-200';
      case APPLICATION_STATUSES.STUDENT_DECLINED: return 'bg-orange-100 text-orange-800 border-orange-200';
      case APPLICATION_STATUSES.REJECTED: return 'bg-red-100 text-red-800 border-red-200';
      case APPLICATION_STATUSES.INTERVIEW: return 'bg-purple-100 text-purple-800 border-purple-200';
      case APPLICATION_STATUSES.TIMEOUT_DECLINED: return 'bg-gray-100 text-gray-800 border-gray-200';
      case APPLICATION_STATUSES.WITHDRAWN: return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case APPLICATION_STATUSES.PENDING: return <FaClock className="mr-1" />;
      case APPLICATION_STATUSES.ACCEPTED: return <FaBell className="mr-1" />;
      case APPLICATION_STATUSES.STUDENT_ACCEPTED: return <FaCheck className="mr-1" />;
      case APPLICATION_STATUSES.STUDENT_DECLINED: return <FaTimes className="mr-1" />;
      case APPLICATION_STATUSES.REJECTED: return <FaTimes className="mr-1" />;
      case APPLICATION_STATUSES.INTERVIEW: return <FaCalendar className="mr-1" />;
      case APPLICATION_STATUSES.TIMEOUT_DECLINED: return <FaExclamationTriangle className="mr-1" />;
      case APPLICATION_STATUSES.WITHDRAWN: return <FaTimes className="mr-1" />;
      default: return <FaClock className="mr-1" />;
    }
  };

  const getStatusDisplayName = (status) => {
    switch (status) {
      case APPLICATION_STATUSES.PENDING: return 'Pending';
      case APPLICATION_STATUSES.ACCEPTED: return 'Awaiting Response';
      case APPLICATION_STATUSES.STUDENT_ACCEPTED: return 'Hired (Confirmed)';
      case APPLICATION_STATUSES.STUDENT_DECLINED: return 'Student Declined';
      case APPLICATION_STATUSES.REJECTED: return 'Rejected';
      case APPLICATION_STATUSES.INTERVIEW: return 'Interview';
      case APPLICATION_STATUSES.TIMEOUT_DECLINED: return 'Auto-Declined';
      case APPLICATION_STATUSES.WITHDRAWN: return 'Withdrawn';
      default: return status;
    }
  };

  // Enhanced Application Card Component
  const ApplicationCard = ({ application }) => {
    const appWithTimeout = getApplicationWithTimeoutInfo(application);
    const hasUrgentTimeout = appWithTimeout.timeoutInfo?.isUrgent;
    
    return (
      <div className={`bg-white rounded-lg shadow-sm border transition-all duration-200 p-4 hover:shadow-md ${
        hasUrgentTimeout ? 'border-red-200 bg-red-50' : 'border-gray-200'
      }`}>
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1">
            <h3 className="font-medium text-gray-900 mb-1">{application.studentName}</h3>
            <p className="text-sm text-gray-600 mb-1">{application.studentEmail}</p>
            <p className="text-sm font-medium text-gray-700">{application.positionTitle}</p>
          </div>
          <div className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(application.status)}`}>
            {getStatusIcon(application.status)}
            {getStatusDisplayName(application.status)}
          </div>
        </div>

        {/* Timeout warning for urgent offers */}
        {hasUrgentTimeout && (
          <div className="bg-red-100 border border-red-200 rounded p-2 mb-3">
            <div className="flex items-center text-red-800 text-xs">
              <FaExclamationTriangle className="mr-1" />
              <span>Offer expires in {appWithTimeout.timeoutInfo.daysUntilDeadline} day{appWithTimeout.timeoutInfo.daysUntilDeadline !== 1 ? 's' : ''}</span>
            </div>
          </div>
        )}

        <div className="text-xs text-gray-500 mb-3">
          Applied: {new Date(application.appliedAt).toLocaleDateString()}
          {application.acceptedAt && (
            <span className="ml-3">
              Accepted: {new Date(application.acceptedAt.toDate()).toLocaleDateString()}
            </span>
          )}
        </div>

        <div className="flex justify-between items-center">
          
          {application.status === APPLICATION_STATUSES.PENDING && (
            <div className="flex space-x-1">
              <button
                onClick={() => updateApplicationStatus(application.id, APPLICATION_STATUSES.ACCEPTED)}
                disabled={updating}
                className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded"
                title="Accept (Send Offer)"
              >
                <FaCheck className="h-3 w-3" />
              </button>
              <button
                onClick={() => updateApplicationStatus(application.id, APPLICATION_STATUSES.INTERVIEW)}
                disabled={updating}
                className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                title="Schedule Interview"
              >
                <FaCalendar className="h-3 w-3" />
              </button>
              <button
                onClick={() => updateApplicationStatus(application.id, APPLICATION_STATUSES.REJECTED)}
                disabled={updating}
                className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                title="Reject"
              >
                <FaTimes className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Chart Components
  const MonthlyApplicationsChart = ({ expanded = false }) => (
    <div className={`bg-white rounded-xl shadow-lg border border-gray-200 ${expanded ? 'fixed inset-4 z-50' : ''}`}>
      <div className="p-6 border-b border-gray-200 flex justify-between items-center">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Monthly Applications Trend</h3>
          <p className="text-sm text-gray-600">Applications received over the last 6 months</p>
        </div>
        <button
          onClick={() => setExpandedChart(expanded ? null : 'monthly')}
          className="p-2 text-gray-400 hover:text-gray-600"
        >
          {expanded ? <FaCompress /> : <FaExpand />}
        </button>
      </div>
      <div className={`p-6 ${expanded ? 'h-96' : 'h-80'}`}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={analyticsData.monthlyApplications}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Area type="monotone" dataKey="applications" stroke="#f97316" fill="#f97316" fillOpacity={0.6} />
            <Area type="monotone" dataKey="studentAccepted" stroke="#10b981" fill="#10b981" fillOpacity={0.6} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );

  const ApplicationStatusChart = ({ expanded = false }) => (
    <div className={`bg-white rounded-xl shadow-lg border border-gray-200 ${expanded ? 'fixed inset-4 z-50' : ''}`}>
      <div className="p-6 border-b border-gray-200 flex justify-between items-center">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Applications by Status</h3>
          <p className="text-sm text-gray-600">Current application distribution</p>
        </div>
        <button
          onClick={() => setExpandedChart(expanded ? null : 'status')}
          className="p-2 text-gray-400 hover:text-gray-600"
        >
          {expanded ? <FaCompress /> : <FaExpand />}
        </button>
      </div>
      <div className={`p-6 ${expanded ? 'h-96' : 'h-80'}`}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={analyticsData.applicationsByStatus}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              outerRadius={expanded ? 120 : 80}
              fill="#8884d8"
              dataKey="value"
            >
              {analyticsData.applicationsByStatus.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );

  const PositionPerformanceChart = ({ expanded = false }) => (
    <div className={`bg-white rounded-xl shadow-lg border border-gray-200 ${expanded ? 'fixed inset-4 z-50' : ''}`}>
      <div className="p-6 border-b border-gray-200 flex justify-between items-center">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Position Performance</h3>
          <p className="text-sm text-gray-600">Applications and confirmed hires by position</p>
        </div>
        <button
          onClick={() => setExpandedChart(expanded ? null : 'positions')}
          className="p-2 text-gray-400 hover:text-gray-600"
        >
          {expanded ? <FaCompress /> : <FaExpand />}
        </button>
      </div>
      <div className={`p-6 ${expanded ? 'h-96' : 'h-80'}`}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={analyticsData.positionPerformance}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis yAxisId="left" />
            <YAxis yAxisId="right" orientation="right" />
            <Tooltip />
            <Legend />
            <Bar yAxisId="left" dataKey="applications" fill="#3b82f6" />
            <Bar yAxisId="left" dataKey="confirmedHires" fill="#10b981" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Welcome Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                Welcome back, {user?.userd?.displayName?.split(' ')[0] || 'Employer'}! 👋
              </h1>
              <p className="text-gray-600">Here's what's happening with your internship program today.</p>
            </div>
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 mt-4 md:mt-0">
              <Link
                to="/employer/positions/add"
                className="flex items-center justify-center bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                <FaPlusCircle className="mr-2" />
                Add New Position
              </Link>
              <Link
                to="/employer/applications"
                className="flex items-center justify-center bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                <FaClipboardList className="mr-2" />
                Manage Applications
              </Link>
            </div>
          </div>

          {/* Analytics View Selector */}
          <div className="flex space-x-2 mb-6">
            <button
              onClick={() => setAnalyticsView('overview')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                analyticsView === 'overview' 
                  ? 'bg-orange-500 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
              }`}
            >
              <FaChartLine className="inline mr-2" />
              Overview
            </button>
            <button
              onClick={() => setAnalyticsView('trends')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                analyticsView === 'trends' 
                  ? 'bg-orange-500 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
              }`}
            >
              <FaChartBar className="inline mr-2" />
              Trends
            </button>
            <button
              onClick={() => setAnalyticsView('positions')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                analyticsView === 'positions' 
                  ? 'bg-orange-500 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
              }`}
            >
              <FaChartPie className="inline mr-2" />
              Positions
            </button>
          </div>

          {/* Alerts */}
          {stats.todayApplications > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <FaBell className="h-5 w-5 text-blue-600 mr-3" />
                <p className="text-blue-800">
                  <strong>{stats.todayApplications}</strong> new application{stats.todayApplications !== 1 ? 's' : ''} received today! 
                  {stats.pendingApplications > 0 && (
                    <span className="ml-2">
                      You have <strong>{stats.pendingApplications}</strong> pending review{stats.pendingApplications !== 1 ? 's' : ''}.
                    </span>
                  )}
                </p>
              </div>
            </div>
          )}

          {stats.urgentOffers > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <FaExclamationTriangle className="h-5 w-5 text-red-600 mr-3" />
                <p className="text-red-800">
                  <strong>{stats.urgentOffers}</strong> offer{stats.urgentOffers !== 1 ? 's' : ''} expiring soon! 
                  Students have less than 3 days to respond.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Enhanced Statistics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 md:gap-6 mb-8">
          {/* Total Positions */}
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-4 md:p-6 rounded-xl shadow-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xs md:text-sm font-medium text-purple-100">Total Positions</h3>
                <p className="text-xl md:text-2xl font-bold">{stats.totalPositions}</p>
                <p className="text-xs text-purple-100">{stats.activePositions} active</p>
              </div>
              <FaBuilding className="h-6 md:h-8 w-6 md:w-8 text-purple-200" />
            </div>
          </div>

          {/* Total Applications */}
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-4 md:p-6 rounded-xl shadow-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xs md:text-sm font-medium text-blue-100">Applications</h3>
                <p className="text-xl md:text-2xl font-bold">{stats.totalApplications}</p>
                <p className="text-xs text-blue-100">{stats.todayApplications} today</p>
              </div>
              <FaUserGraduate className="h-6 md:h-8 w-6 md:w-8 text-blue-200" />
            </div>
          </div>

          {/* Pending Reviews */}
          <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 p-4 md:p-6 rounded-xl shadow-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xs md:text-sm font-medium text-yellow-100">Pending</h3>
                <p className="text-xl md:text-2xl font-bold">{stats.pendingApplications}</p>
                <p className="text-xs text-yellow-100">Need review</p>
              </div>
              <FaClock className="h-6 md:h-8 w-6 md:w-8 text-yellow-200" />
            </div>
          </div>

          {/* Awaiting Student Response */}
          <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 p-4 md:p-6 rounded-xl shadow-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xs md:text-sm font-medium text-cyan-100">Awaiting Response</h3>
                <p className="text-xl md:text-2xl font-bold">{stats.awaitingResponseApplications}</p>
                <p className="text-xs text-cyan-100">
                  {stats.urgentOffers > 0 ? `${stats.urgentOffers} urgent` : 'Offers sent'}
                </p>
              </div>
              <FaBell className="h-6 md:h-8 w-6 md:w-8 text-cyan-200" />
            </div>
          </div>

          {/* Interviews Scheduled */}
          <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 p-4 md:p-6 rounded-xl shadow-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xs md:text-sm font-medium text-indigo-100">Interviews</h3>
                <p className="text-xl md:text-2xl font-bold">{stats.interviewApplications}</p>
                <p className="text-xs text-indigo-100">Scheduled</p>
              </div>
              <FaCalendar className="h-6 md:h-8 w-6 md:w-8 text-indigo-200" />
            </div>
          </div>

          {/* Confirmed Hires */}
          <div className="bg-gradient-to-br from-green-500 to-green-600 p-4 md:p-6 rounded-xl shadow-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xs md:text-sm font-medium text-green-100">Confirmed Hires</h3>
                <p className="text-xl md:text-2xl font-bold">{stats.confirmedHires}</p>
                <p className="text-xs text-green-100">{stats.conversionRate.toFixed(1)}% rate</p>
              </div>
              <FaUserCheck className="h-6 md:h-8 w-6 md:w-8 text-green-200" />
            </div>
          </div>
        </div>

        {/* Analytics Dashboard */}
        {analyticsView === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <MonthlyApplicationsChart expanded={expandedChart === 'monthly'} />
            <ApplicationStatusChart expanded={expandedChart === 'status'} />
          </div>
        )}

        {analyticsView === 'trends' && (
          <div className="space-y-6 mb-8">
            <MonthlyApplicationsChart expanded={expandedChart === 'monthly'} />
            
            {/* Weekly Trends Chart */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-bold text-gray-900">Weekly Application Trends</h3>
                <p className="text-sm text-gray-600">Application volume over the last 8 weeks</p>
              </div>
              <div className="p-6 h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={analyticsData.applicationTrends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="week" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="applications" stroke="#f97316" strokeWidth={3} />
                    <Line type="monotone" dataKey="confirmedHires" stroke="#10b981" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Hiring Funnel */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-bold text-gray-900">Hiring Funnel</h3>
                <p className="text-sm text-gray-600">Conversion rates through your hiring process</p>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {analyticsData.hiringFunnel.map((stage, index) => (
                    <div key={stage.stage} className="flex items-center">
                      <div className="w-32 text-sm font-medium text-gray-700">{stage.stage}</div>
                      <div className="flex-1 mx-4">
                        <div className="bg-gray-200 rounded-full h-6">
                          <div 
                            className="bg-orange-500 h-6 rounded-full flex items-center justify-center text-white text-xs font-medium"
                            style={{ width: `${stage.percentage}%` }}
                          >
                            {stage.percentage.toFixed(1)}%
                          </div>
                        </div>
                      </div>
                      <div className="w-16 text-sm text-gray-600 text-right">{stage.count}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {analyticsView === 'positions' && (
          <div className="space-y-6 mb-8">
            <PositionPerformanceChart expanded={expandedChart === 'positions'} />
            
            {/* Position Analytics Table */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-bold text-gray-900">Detailed Position Analytics</h3>
                <p className="text-sm text-gray-600">Performance metrics for all your positions</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Position</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Applications</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Confirmed Hires</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Conversion Rate</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {analyticsData.positionPerformance.map((position, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{position.fullName}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{position.location}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{position.applications}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{position.confirmedHires}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="text-sm text-gray-900">{position.conversionRate.toFixed(1)}%</div>
                            <div className="ml-2 w-16 bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-orange-500 h-2 rounded-full"
                                style={{ width: `${Math.min(position.conversionRate, 100)}%` }}
                              ></div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Main Content Grid - Recent Applications and Sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Recent Applications */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-lg border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
                <h2 className="text-xl font-bold text-gray-900">Recent Applications</h2>
                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                  <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search applications..."
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                    />
                  </div>
                  <select
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                  >
                    <option value="all">All Status</option>
                    <option value={APPLICATION_STATUSES.PENDING}>Pending</option>
                    <option value={APPLICATION_STATUSES.INTERVIEW}>Interview</option>
                    <option value={APPLICATION_STATUSES.ACCEPTED}>Awaiting Response</option>
                    <option value={APPLICATION_STATUSES.STUDENT_ACCEPTED}>Hired (Confirmed)</option>
                    <option value={APPLICATION_STATUSES.STUDENT_DECLINED}>Student Declined</option>
                    <option value={APPLICATION_STATUSES.REJECTED}>Rejected</option>
                    <option value={APPLICATION_STATUSES.TIMEOUT_DECLINED}>Auto-Declined</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="p-6">
              {filteredApplications.length === 0 ? (
                <div className="text-center py-12">
                  <FaUserGraduate className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No applications found</h3>
                  <p className="text-gray-600">
                    {applications.length === 0 
                      ? "No applications have been received yet."
                      : "No applications match your current filters."
                    }
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {filteredApplications.slice(0, 6).map(application => (
                    <ApplicationCard key={application.id} application={application} />
                  ))}
                </div>
              )}

              {filteredApplications.length > 6 && (
                <div className="mt-6 text-center">
                  <Link
                    to="/employer/applications"
                    className="inline-flex items-center text-orange-600 hover:text-orange-800 font-medium"
                  >
                    View All Applications ({filteredApplications.length})
                    <FaArrowUp className="ml-1 h-4 w-4 transform rotate-45" />
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar Content */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-bold text-gray-900">Quick Actions</h3>
              </div>
              <div className="p-6 space-y-4">
                <Link
                  to="/employer/positions"
                  className="flex items-center p-4 bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg hover:from-orange-100 hover:to-orange-200 transition-all group"
                >
                  <div className="bg-orange-500 p-3 rounded-lg mr-4">
                    <FaBuilding className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 group-hover:text-orange-700">Manage Positions</h4>
                    <p className="text-sm text-gray-600">View and edit job postings</p>
                  </div>
                </Link>

                <Link
                  to="/employer/manage-interns"
                  className="flex items-center p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg hover:from-blue-100 hover:to-blue-200 transition-all group"
                >
                  <div className="bg-blue-500 p-3 rounded-lg mr-4">
                    <FaUsers className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 group-hover:text-blue-700">Manage Interns</h4>
                    <p className="text-sm text-gray-600">Track intern progress</p>
                  </div>
                </Link>

                <Link
                  to="/employer/applications"
                  className="flex items-center p-4 bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg cursor-pointer hover:from-purple-100 hover:to-purple-200 transition-all group"
                >
                  <div className="bg-purple-500 p-3 rounded-lg mr-4">
                    <FaChartLine className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 group-hover:text-purple-700">Manage Applications</h4>
                    <p className="text-sm text-gray-600">Review potential interns</p>
                  </div>
                </Link>
              </div>
            </div>

            {/* Top Positions */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-bold text-gray-900">Top Performing Positions</h3>
              </div>
              <div className="p-6">
                {topPositions.length === 0 ? (
                  <div className="text-center py-8">
                    <FaBriefcase className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                    <p className="text-gray-600 text-sm">No positions created yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {topPositions.map((position, index) => (
                      <div key={position.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center">
                          <div className="bg-orange-100 text-orange-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">
                            {index + 1}
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900 text-sm">{position.name}</h4>
                            <p className="text-xs text-gray-600">{position.location}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-900">{position.applicationCount}</p>
                          <p className="text-xs text-gray-600">applications</p>
                          {position.confirmedHires > 0 && (
                            <p className="text-xs text-green-600">{position.confirmedHires} hired</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-bold text-gray-900">Recent Activity</h3>
              </div>
              <div className="p-6">
                {recentActivity.length === 0 ? (
                  <div className="text-center py-8">
                    <FaBell className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                    <p className="text-gray-600 text-sm">No recent activity</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {recentActivity.slice(0, 5).map((activity) => (
                      <div key={activity.id} className={`flex items-start space-x-3 ${activity.isUrgent ? 'bg-red-50 p-2 rounded' : ''}`}>
                        <div className={`flex-shrink-0 w-2 h-2 rounded-full mt-2 ${
                          activity.status === APPLICATION_STATUSES.PENDING ? 'bg-yellow-400' :
                          activity.status === APPLICATION_STATUSES.STUDENT_ACCEPTED ? 'bg-green-400' :
                          activity.status === APPLICATION_STATUSES.REJECTED ? 'bg-red-400' :
                          activity.status === APPLICATION_STATUSES.ACCEPTED ? 'bg-blue-400' :
                          'bg-purple-400'
                        }`}></div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900">{activity.message}</p>
                          <p className="text-xs text-gray-500">{new Date(activity.time).toLocaleDateString()}</p>
                          {activity.isUrgent && (
                            <p className="text-xs text-red-600 font-medium">⚠️ Urgent response needed</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Expanded Chart Overlay */}
        {expandedChart && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setExpandedChart(null)} />
        )}
      </div>
    </div>
  );
};

export default Dashboard;